import { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, History } from 'lucide-react';
import { useLocalization } from '@/hooks/useLocalization';

interface ScoreHistoryRow {
  score: number;
  hitl_rate: number;
  correction_frequency: number;
  computed_at: string;
}

const MAX_POINTS = 30;

export const GovernanceScoreHistory = () => {
  const { t } = useLocalization();
  const { tenantId } = useAuth();
  const [rows, setRows] = useState<ScoreHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let query = supabase
      .from('governance_score_history')
      .select('score, hitl_rate, correction_frequency, computed_at')
      .order('computed_at', { ascending: false })
      .limit(MAX_POINTS);

    // Scope by tenant when the claim is available; otherwise rely on the
    // same RLS bypass every other tenant-scoped query in this app falls
    // back to when auth_tenant_id() is NULL (see Sprint I.1 notes).
    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    query.then(({ data, error }) => {
      if (!error && data) setRows([...data].reverse());
      setLoading(false);
    });
  }, [tenantId]);

  const latest = rows[rows.length - 1];
  const chartData = rows.map((r) => ({
    ...r,
    label: new Date(r.computed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4 text-primary" />
          
          {t('Governance Score Trend')}
        </CardTitle>
        <CardDescription>
          
          {t('Recorded on every AI output creation and HITL review action')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-xs text-muted-foreground text-center px-2">
            
            {t('No score history yet — a snapshot is recorded the next time an AI output is created or reviewed.')}
          </div>
        ) : (
          <>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    width={30}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number, name: string) => [
                      name === 'score' ? value : `${Number(value).toFixed(1)}%`,
                      name === 'score' ? 'Score' : name === 'hitl_rate' ? 'HITL Rate' : 'Correction Freq.',
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(160, 84%, 39%)"
                    fill="url(#scoreGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {latest && (
              <div className="mt-4 grid grid-cols-2 gap-2 text-center text-xs">
                <div className="rounded bg-muted/50 px-2 py-1.5">
                  <div className="font-semibold text-foreground">{latest.hitl_rate.toFixed(0)}%</div>
                  <div className="text-muted-foreground">{t('HITL Review Rate')}</div>
                </div>
                <div className="rounded bg-muted/50 px-2 py-1.5">
                  <div className="font-semibold text-foreground">{latest.correction_frequency.toFixed(1)}</div>
                  <div className="text-muted-foreground">{t('Correction Freq. (30d)')}</div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GovernanceScoreHistory;
